import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { GlobalErrorHandler } from '@salesrun/ngx-logging/services/global.error-handler';
import { catchError, EMPTY, Observable, switchMap, throwError, timer } from 'rxjs';
import {
    ConnectionLostError,
    HttpRequestError,
    RequestTimeoutError,
    UnauthenticatedError,
    UnauthorizedError,
} from '../errors';
import { NotFoundError } from '../errors/NotFoundError';
import { ValidationError } from '../errors/ValidationError';
import { ERROR_TRANSFORMS } from '../logging.constants';
import { IErrorTransform } from '../models/IErrorTransform';
import { RetryCountHttpParams } from '../models/RetryCountHttpParams';


@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

    private readonly RETRY_LIMIT = 5;
    private readonly CONNECTION_LOST_CODES = [0, 424, 503];
    private readonly TIMEOUT_STATUS_CODES = [408, 502, 504, 520, 522, 524];
    private readonly RETRY_STATUS_CODES = [0, ...this.TIMEOUT_STATUS_CODES];

    constructor(
        private _errorHandler: GlobalErrorHandler,
        @Inject(ERROR_TRANSFORMS) @Optional() private _customTransforms: IErrorTransform[],
    ) { }

    intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return this.sendRequest(req, next);
    }

    private sendRequest(req: HttpRequest<unknown>, next: HttpHandler, count = 0): Observable<HttpEvent<unknown>> {
        if (this._errorHandler.connectionLost) {
            return EMPTY;
        }
        if (count > 0) {
            req = req.clone({ setParams: { 'retry': count.toString() } });
        }
        return next.handle(req)
            .pipe(
                catchError((response) => {
                    if (response instanceof HttpErrorResponse) {
                        let retryLimit = this.RETRY_LIMIT;
                        if (req.params.has(RetryCountHttpParams.paramName)) {
                            retryLimit = +req.params.get(RetryCountHttpParams.paramName);
                        }
                        if (this.RETRY_STATUS_CODES.includes(response.status)
                            && req.method === 'GET'
                            && count < retryLimit
                            && !this._errorHandler.connectionLost
                        ) {
                            return timer(2000).pipe(
                                switchMap(() => this.sendRequest(req, next, ++count)),
                            );
                        }

                        if (response.status === 401) {
                            return throwError(() => new UnauthenticatedError(this.getMessageFromCustomError(response)));
                        }

                        if (response.status === 403) {
                            return throwError(() => new UnauthorizedError(this.getMessageFromCustomError(response)));
                        }

                        if (response.status === 404) {
                            return throwError(() => new NotFoundError(req.url, response.error));
                        }

                        if (response.status === 422) {
                            return throwError(() => new ValidationError(this.getMessageFromCustomError(response)));
                        }

                        if (this.CONNECTION_LOST_CODES.includes(response.status)) {
                            return throwError(() => new ConnectionLostError(req.url));
                        }

                        if (this.TIMEOUT_STATUS_CODES.includes(response.status)) {
                            return throwError(() => new RequestTimeoutError(req.url));
                        }

                        return this.createAndThrowError(response);

                    }
                    return throwError(() => response);
                }),
                catchError(error => {
                    if (this._customTransforms) {
                        error = this._customTransforms.reduce((err, transform) => transform(err), error);
                    }
                    return throwError(() => error);
                }),
            );
    }

    private getMessageFromCustomError(response: HttpErrorResponse): string {
        const message = response.error?.reason ?? response.error;
        if (typeof message == 'string') {
            return message;
        }
        return response.message;
    }

    private async createAndThrowError(response: HttpErrorResponse): Promise<never> {
        let details = null;
        if (response.error instanceof Blob) {
            details = await (response.error).text();
        } else {
            details = response.error;
        }
        throw new HttpRequestError(response.message, response.status, details);
    }
}
